import { cn, formatRank, getRankScore } from "@/lib/utils";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { addLeaderboardMember, ApiError, createLeaderboard, deleteLeaderboard, fetchLeaderboard, fetchLeaderboardLive, removeLeaderboardMember, renameLeaderboard } from "../api";
import type { Leaderboard, LeaderboardEvent, LeaderboardGroup, LeaderboardLiveGame, LeaderboardMember, LeaderboardRange, PlayerRank, RangeChange } from "../types";
import { getChampionIconUrl, getChampionName, getQueueLabel, getRankImage, getSummonerIconUrl, isApexTier } from "../utils/ddragon";
import { getSavedRegion, parseRiotId, REGIONS, saveRegion, summonerPath } from "../utils/regions";
import { formatRelativeTime, winrateColor } from "./summoner/parts";
import { button, label, Skeleton } from "./ui/primitives";

// No accounts: boards this browser has opened are remembered here, with the edit key when it has one.
const SAVED_KEY = "riftwatch.leaderboards";
interface SavedBoard {
	slug: string;
	name: string;
	editKey?: string;
}

function savedBoards(): SavedBoard[] {
	try {
		return JSON.parse(localStorage.getItem(SAVED_KEY) ?? "[]") as SavedBoard[];
	} catch {
		return [];
	}
}

/** editKey: undefined keeps the stored one, null forgets it. A null patch forgets the board. */
function saveBoard(slug: string, patch: { name?: string; editKey?: string | null } | null) {
	const all = savedBoards();
	const prev = all.find(x => x.slug === slug);
	const rest = all.filter(x => x.slug !== slug);
	const next = patch && { slug, name: patch.name ?? prev?.name ?? "", editKey: patch.editKey === null ? undefined : (patch.editKey ?? prev?.editKey) };
	try {
		localStorage.setItem(SAVED_KEY, JSON.stringify(next ? [next, ...rest] : rest));
	} catch {
		/* not persisted */
	}
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

type Queue = "solo" | "flex" | "premade";
const QUEUE_LABEL: Record<Queue, string> = { solo: "Solo/Duo", flex: "Flex", premade: "Ranked 5s" };
const QUEUE_SHORT: Record<Queue, string> = { solo: "Solo", flex: "Flex", premade: "5s" };
const SORT_GROUPS = [
	{
		label: "Rank",
		sorts: [
			["solo", "Solo/Duo"],
			["flex", "Flex"],
			["premade", "Ranked 5s"]
		]
	},
	{
		label: "LP gained",
		sorts: [
			["week", "Total"],
			["weekSolo", "Solo/Duo"],
			["weekFlex", "Flex"],
			["weekPremade", "Ranked 5s"]
		]
	}
] as const;
type Sort = (typeof SORT_GROUPS)[number]["sorts"][number][0];
const SORT_KEY = "riftwatch.leaderboard.sort";
/** The queues each "LP gained" sort adds up. */
const WEEK: Partial<Record<Sort, Queue[]>> = { week: ["solo", "flex", "premade"], weekSolo: ["solo"], weekFlex: ["flex"], weekPremade: ["premade"] };
/** The queue a sort is about: its rank, its "LP gained", its groups card. The total counts as Solo/Duo there. */
const queueOf = (sort: Sort): Queue => (WEEK[sort]?.length === 1 ? WEEK[sort]![0]! : WEEK[sort] ? "solo" : (sort as Queue));

function savedSort(): Sort {
	const s = localStorage.getItem(SORT_KEY);
	return SORT_GROUPS.some(g => g.sorts.some(([k]) => k === s)) ? (s as Sort) : "solo";
}

const RANGES: Array<[LeaderboardRange, string, string]> = [
	["1d", "24h", "24 hours"],
	["7d", "7d", "7 days"],
	["30d", "30d", "30 days"]
];
const RANGE_KEY = "riftwatch.leaderboard.range";
const rangeText = (r: LeaderboardRange) => RANGES.find(x => x[0] === r)![2];

function savedRange(): LeaderboardRange {
	const r = localStorage.getItem(RANGE_KEY);
	return RANGES.some(x => x[0] === r) ? (r as LeaderboardRange) : "7d";
}

/** LP over the range summed over `queues`; null when none of them has any data. */
const weekLp = (m: LeaderboardMember, queues: Queue[]) => (queues.every(q => m.change[q]?.lp == null) ? null : queues.reduce((sum, q) => sum + (m.change[q]?.lp ?? 0), 0));

function sorted(members: LeaderboardMember[], sort: Sort) {
	const week = WEEK[sort];
	const score = (m: LeaderboardMember) => (week ? (weekLp(m, week) ?? -Infinity) : getRankScore(m[sort as Queue]));
	return [...members].sort((a, b) => score(b) - score(a) || a.summoner.name.localeCompare(b.summoner.name));
}

const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${Math.abs(n)}`;
const ranked = (r: PlayerRank | null): r is PlayerRank => !!r && r.tier !== "UNRANKED";

function RankCell({ rank }: { rank: PlayerRank | null }) {
	if (!ranked(rank)) return <span className="text-[13px] text-ink-3">Unranked</span>;
	const total = rank.wins + rank.losses;
	const wr = total ? Math.round((rank.wins / total) * 100) : 0;
	return (
		<span className="flex items-center gap-2.5">
			<img src={getRankImage(rank.tier)} alt="" className="hidden h-9 w-9 shrink-0 object-contain sm:block" />
			<span className="min-w-0">
				<span className="block text-sm font-bold">
					{isApexTier(rank.tier) ? formatRank(rank).replace(/ \d+ LP$/, "") : formatRank(rank)}
					<span className="ml-1.5 font-mono text-[13px] text-ink-2">{rank.leaguePoints} LP</span>
				</span>
				<span className="block text-xs text-ink-3">
					{rank.wins}W {rank.losses}L <span className={cn("font-bold", winrateColor(wr))}>{wr}%</span>
				</span>
			</span>
		</span>
	);
}

function WeekPart({ name, w }: { name: string; w: RangeChange | null }) {
	if (!w || (!w.lp && !w.wins && !w.losses)) return null;
	return (
		<span>
			{name} {w.lp !== null && <span className={cn("font-mono font-bold", w.lp > 0 ? "text-signal" : "text-ink-2")}>{signed(w.lp)}</span>} · {w.wins}W {w.losses}L
		</span>
	);
}

function WeekCell({ m, queues }: { m: LeaderboardMember; queues: Queue[] }) {
	const lp = weekLp(m, queues);
	return (
		<span className="block text-right">
			<span className={cn("block font-mono text-[17px] font-extrabold", lp && lp > 0 ? "text-signal" : "text-ink")}>{lp === null ? "–" : `${signed(lp)} LP`}</span>
			<span className="flex flex-col items-end text-xs text-ink-3 sm:flex-row sm:gap-2">
				{queues.map(q => (
					<WeekPart key={q} name={QUEUE_SHORT[q]} w={m.change[q]} />
				))}
			</span>
		</span>
	);
}

/**
 * The member's icon, or while they are in a game their champion with a pulsing dot (links to the live tab), so the row
 * keeps its height. Minutes only: the list refreshes every couple of minutes, not every second.
 */
function Avatar({ m, live }: { m: LeaderboardMember; live?: LeaderboardLiveGame }) {
	const s = m.summoner;
	if (!live) return <img src={getSummonerIconUrl(s.profileIconId)} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-lg" />;
	const minutes = Math.max(0, Math.floor((Date.now() - live.gameStartTime) / 60_000));
	const text = `In game · ${getChampionName(live.championId)} · ${getQueueLabel(live.queueId)} · ${live.gameStartTime > 0 ? `${minutes}m` : "loading in"}`;
	return (
		<Link to={`${summonerPath(s.region, s.name, s.tagLine)}?tab=live`} title={text} aria-label={`${s.name}: ${text}`} className="relative shrink-0">
			<img src={getChampionIconUrl(live.championId)} alt="" className="h-9 w-9 rounded-lg ring-2 ring-signal" />
			<span className="absolute -right-1 -top-1 h-2.5 w-2.5" aria-hidden="true">
				<span className="absolute inset-0 rounded-full bg-signal animate-ping-soft" />
				<span className="absolute inset-0 rounded-full border-2 border-surface bg-signal" />
			</span>
		</Link>
	);
}

function Row({ m, place, sort, live, onRemove }: { m: LeaderboardMember; place: number; sort: Sort; live?: LeaderboardLiveGame; onRemove?: () => void }) {
	const s = m.summoner;
	const week = WEEK[sort];
	// the rank shown: the sort's queue, or for the total the first queue the member is ranked in
	const rank = sort === "week" ? ([m.solo, m.flex, m.premade].find(ranked) ?? null) : m[queueOf(sort)];
	return (
		<li className="grid grid-cols-[20px_minmax(0,1fr)_auto_auto] items-center gap-2.5 border-t border-line px-3 py-2.5 first:border-t-0 sm:grid-cols-[28px_minmax(0,1fr)_minmax(0,220px)_auto] sm:gap-3 sm:px-3.5">
			<span className={cn("text-center font-mono text-sm font-bold", place === 1 ? "text-signal" : "text-ink-3")}>{place}</span>
			<span className="flex min-w-0 items-center gap-2.5">
				<Avatar m={m} live={live} />
				<span className="min-w-0">
					<Link to={summonerPath(s.region, s.name, s.tagLine)} className="block truncate text-sm font-semibold hover:text-signal">
						{s.name}
						<span className="font-medium text-ink-3">#{s.tagLine}</span>
					</Link>
					<Form m={m} />
				</span>
			</span>
			<span className={cn(week && "hidden sm:block")}>
				<RankCell rank={rank} />
			</span>
			<span className="flex items-center justify-end gap-2">
				{week && <WeekCell m={m} queues={week} />}
				{onRemove && (
					<button type="button" onClick={onRemove} aria-label={`Remove ${s.name}#${s.tagLine}`} className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-ink-3 hover:bg-raised hover:text-ink">
						<X aria-hidden="true" className="h-4 w-4" />
					</button>
				)}
			</span>
		</li>
	);
}

/** Last ranked games as bars, oldest left, plus a badge for 3+ wins in a row. Losses stay neutral (Riot policy: no shaming). */
function Form({ m }: { m: LeaderboardMember }) {
	if (!m.form.length) return <span className="block text-[11px] text-ink-3">No ranked games yet</span>;
	const wins = m.form.filter(g => g.win).length;
	return (
		<span className="mt-1 flex items-center gap-2 overflow-hidden">
			<span role="img" aria-label={`Last ${m.form.length} ranked games: ${wins} wins, ${m.form.length - wins} losses`} className="flex gap-0.5">
				{[...m.form].reverse().map(g => (
					<span key={g.matchId} title={`${g.win ? "Win" : "Defeat"} · ${getChampionName(g.championId)}`} className={cn("h-3 w-[5px] rounded-[1px]", g.win ? "bg-signal" : "bg-line-2")} />
				))}
			</span>
			{m.streak >= 3 && (
				<span className="whitespace-nowrap text-[11px] font-bold text-signal" title={`${m.streak} wins in a row`}>
					{m.streak}W<span className="hidden sm:inline"> streak</span>
				</span>
			)}
		</span>
	);
}

const who = (members: Map<number, LeaderboardMember>, id: number) => members.get(id)?.summoner.name ?? "Someone";

/** "A", "A & B", "A, B & C" */
const names = (members: Map<number, LeaderboardMember>, ids: number[]) => {
	const n = ids.map(id => who(members, id));
	return n.length < 2 ? n.join("") : `${n.slice(0, -1).join(", ")} & ${n.at(-1)}`;
};

function GroupsCard({ groups, queue, members, range }: { groups: LeaderboardGroup[]; queue: Queue; members: Map<number, LeaderboardMember>; range: LeaderboardRange }) {
	return (
		<div className="rounded-[10px] border border-line bg-surface p-3.5">
			<h2 className={cn(label, "m-0 mb-2.5")}>
				{queue === "solo" ? "Solo/Duo duos" : `${QUEUE_LABEL[queue]} premades`} · {rangeText(range)}
			</h2>
			{groups.length ? (
				<ul className="m-0 flex list-none flex-col gap-2.5 p-0">
					{groups.map(g => {
						const wr = Math.round((g.wins / g.games) * 100);
						return (
							<li key={g.ids.join(":")} className="flex items-center gap-2.5 text-[13px]">
								<span className="flex shrink-0">
									{g.ids.map((id, i) => (
										<img key={id} src={getSummonerIconUrl(members.get(id)?.summoner.profileIconId ?? 29)} alt="" loading="lazy" className={cn("h-7 w-7 rounded-md border-2 border-surface", i && "-ml-2")} />
									))}
								</span>
								<span className="min-w-0 flex-1 truncate font-semibold" title={names(members, g.ids)}>
									{names(members, g.ids)}
								</span>
								<span className="whitespace-nowrap text-xs text-ink-3">
									{g.games} {g.games === 1 ? "game" : "games"} · {g.wins}W {g.games - g.wins}L <span className={cn("font-bold", winrateColor(wr))}>{wr}%</span>
								</span>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="m-0 text-sm text-ink-2">No {QUEUE_LABEL[queue]} games together in the last {rangeText(range)}.</p>
			)}
		</div>
	);
}

const tierName = (tier: string, rank: string) => formatRank({ tier, rank, leaguePoints: 0, wins: 0, losses: 0 }).replace(/ 0 LP$/, "");

function eventText(e: LeaderboardEvent) {
	if (e.kind === "pentakill") return `got a pentakill as ${getChampionName(e.championId)}`;
	const queue = QUEUE_LABEL[e.queue];
	return `${e.kind === "placed" ? "placed into" : e.kind === "promoted" ? "promoted to" : "dropped to"} ${tierName(e.tier, e.rank)} in ${queue}`;
}

function FeedCard({ feed, members, range }: { feed: LeaderboardEvent[]; members: Map<number, LeaderboardMember>; range: LeaderboardRange }) {
	return (
		<div className="rounded-[10px] border border-line bg-surface p-3.5">
			<h2 className={cn(label, "m-0 mb-2.5")}>Activity · {rangeText(range)}</h2>
			{feed.length ? (
				<ul className="m-0 flex list-none flex-col gap-2.5 p-0">
					{feed.map(e => (
						<li key={`${e.kind}:${e.playerId}:${e.t}`} className="flex items-center gap-2.5 text-[13px]">
							<img
								src={e.kind === "pentakill" ? getChampionIconUrl(e.championId) : getRankImage(e.tier)}
								alt=""
								loading="lazy"
								className={cn("h-7 w-7 shrink-0", e.kind === "pentakill" ? "rounded-md" : "object-contain")}
							/>
							<span className="min-w-0 flex-1">
								<span className="font-semibold">{who(members, e.playerId)}</span> <span className="text-ink-2">{eventText(e)}</span>
							</span>
							<span className="whitespace-nowrap text-xs text-ink-3">{formatRelativeTime(new Date(e.t).toISOString())}</span>
						</li>
					))}
				</ul>
			) : (
				<p className="m-0 text-sm text-ink-2">No rank changes or pentakills in the last {rangeText(range)}.</p>
			)}
		</div>
	);
}

function CopyButton({ text, children }: { text: string; children: string }) {
	const [done, setDone] = useState(false);
	return (
		<button
			type="button"
			className={button.secondary}
			onClick={() =>
				navigator.clipboard.writeText(text).then(() => {
					setDone(true);
					setTimeout(() => setDone(false), 1500);
				})
			}
		>
			{done ? "Copied" : children}
		</button>
	);
}

function AddForm({ onAdd, disabled }: { onAdd: (region: string, riotId: string) => Promise<void>; disabled: boolean }) {
	const [region, setRegion] = useState(getSavedRegion);
	const [text, setText] = useState("");
	const [busy, setBusy] = useState(false);
	const id = parseRiotId(text);
	return (
		<form
			className="flex flex-wrap items-center gap-2"
			onSubmit={async e => {
				e.preventDefault();
				if (!id) return;
				setBusy(true);
				try {
					await onAdd(region, `${id.name}#${id.tag}`);
					setText("");
				} finally {
					setBusy(false);
				}
			}}
		>
			<input
				value={text}
				onChange={e => setText(e.target.value)}
				placeholder="Name#TAG"
				aria-label="Riot ID to add"
				className="h-10 min-w-0 flex-[1_1_200px] rounded-lg border border-line-2 bg-raised px-3 text-sm text-ink placeholder:text-ink-3"
			/>
			<select
				value={region}
				aria-label="Region"
				onChange={e => {
					setRegion(e.target.value);
					saveRegion(e.target.value);
				}}
				className="h-10 cursor-pointer rounded-lg border border-line-2 bg-raised px-2.5 text-[13px] font-bold text-ink"
			>
				{REGIONS.map(r => (
					<option key={r.slug} value={r.slug}>
						{r.label}
					</option>
				))}
			</select>
			<button type="submit" disabled={!id || busy || disabled} className={cn(button.primary, "h-10")}>
				{busy ? "Adding…" : "Add player"}
			</button>
		</form>
	);
}

/** Matches the server's edge cache on /live: polling faster would only get the same answer. */
const LIVE_MS = 120_000;

function Board({ slug }: { slug: string }) {
	const navigate = useNavigate();
	const [editKey, setEditKey] = useState(() => savedBoards().find(b => b.slug === slug)?.editKey);
	const [data, setData] = useState<Leaderboard | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [sort, setSort] = useState<Sort>(savedSort);
	const [range, setRange] = useState<LeaderboardRange>(savedRange);
	const [renaming, setRenaming] = useState<string | null>(null);
	const [live, setLive] = useState(new Map<number, LeaderboardLiveGame>());

	const show = useCallback(
		(b: Leaderboard) => {
			setData(b);
			setError(null);
			saveBoard(slug, { name: b.name });
		},
		[slug]
	);

	useEffect(() => {
		// an edit link carries its key in the fragment (never sent to the server): keep it, then tidy the address bar
		const fromLink = new URLSearchParams(window.location.hash.slice(1)).get("key") ?? undefined;
		if (fromLink) history.replaceState(null, "", window.location.pathname);
		const key = fromLink ?? savedBoards().find(b => b.slug === slug)?.editKey;
		// (a range switch keeps the old numbers on screen until the new ones arrive)
		fetchLeaderboard(slug, range, key)
			.then(b => {
				// a key that no longer fits (board recreated, typo) is dropped instead of showing broken edit controls
				setEditKey(b.editable ? key : undefined);
				saveBoard(slug, { editKey: b.editable ? key : null });
				show(b);
			})
			.catch(e => setError(errorText(e, "Could not load the leaderboard")));
	}, [slug, range, show]);

	// who is in a game: now, then every LIVE_MS while the tab is visible. An answer older than two polls (tab was
	// hidden, a poll failed or hangs on the bot check) is dropped: no badge beats a game that ended long ago.
	useEffect(() => {
		let answeredAt = 0;
		const poll = () => {
			if (document.hidden) return;
			if (Date.now() - answeredAt > 2 * LIVE_MS) setLive(new Map());
			fetchLeaderboardLive(slug)
				.then(r => {
					answeredAt = Date.now();
					setLive(new Map(r.inGame.map(g => [g.playerId, g])));
				})
				.catch(() => setLive(new Map()));
		};
		poll();
		const timer = setInterval(poll, LIVE_MS);
		document.addEventListener("visibilitychange", poll);
		return () => {
			clearInterval(timer);
			document.removeEventListener("visibilitychange", poll);
		};
	}, [slug]);

	const act = (fn: () => Promise<Leaderboard>, fallback: string) =>
		fn()
			.then(show)
			.catch(e => setError(errorText(e, fallback)));

	const pick = (s: Sort) => {
		setSort(s);
		try {
			localStorage.setItem(SORT_KEY, s);
		} catch {
			/* not persisted */
		}
	};
	const pickRange = (r: LeaderboardRange) => {
		setRange(r);
		try {
			localStorage.setItem(RANGE_KEY, r);
		} catch {
			/* not persisted */
		}
	};

	if (error && !data) {
		return (
			<div role="alert" className="mt-8 max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
				{error} <Link to="/leaderboard" className="font-semibold text-signal">Your leaderboards</Link>
			</div>
		);
	}
	if (!data) return <Skeleton className="mt-8 h-[420px] rounded-[10px]" />;

	const viewUrl = `${window.location.origin}/leaderboard/${slug}`;
	const full = data.members.length >= data.maxMembers;
	const byId = new Map(data.members.map(m => [m.id, m]));
	// the groups card follows the queue being ranked; the totals view shows Solo/Duo
	const groupQueue = queueOf(sort);
	return (
		<>
			<div className="mb-5 flex flex-wrap items-end justify-between gap-3">
				{renaming !== null && editKey ? (
					<form
						className="flex flex-wrap items-center gap-2"
						onSubmit={e => {
							e.preventDefault();
							act(() => renameLeaderboard(slug, range, editKey, renaming), "Could not rename").then(() => setRenaming(null));
						}}
					>
						<input value={renaming} onChange={e => setRenaming(e.target.value)} maxLength={40} aria-label="Leaderboard name" autoFocus className="h-11 rounded-lg border border-line-2 bg-raised px-3 text-lg font-bold text-ink" />
						<button type="submit" className={button.secondary}>Save</button>
						<button type="button" className={button.secondary} onClick={() => setRenaming(null)}>Cancel</button>
					</form>
				) : (
					<h1 className="m-0 text-[30px] font-extrabold leading-[1.05] [font-stretch:118%] sm:text-[40px]">
						{data.name}
						{editKey && (
							<button type="button" onClick={() => setRenaming(data.name)} className="ml-3 cursor-pointer align-middle text-[13px] font-semibold text-ink-3 hover:text-ink">
								Rename
							</button>
						)}
					</h1>
				)}
				<div className="flex flex-wrap gap-2">
					<CopyButton text={viewUrl}>Copy view link</CopyButton>
					{editKey && <CopyButton text={`${viewUrl}#key=${editKey}`}>Copy edit link</CopyButton>}
				</div>
			</div>

			<div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2">
				{SORT_GROUPS.map(g => (
					<div key={g.label} role="group" aria-label={`Sort by ${g.label.toLowerCase()}`} className="flex flex-wrap items-center gap-1.5">
						<span className={cn(label, "mr-1")}>{g.label}</span>
						{g.sorts.map(([s, name]) => (
							<button
								key={s}
								type="button"
								aria-pressed={sort === s}
								onClick={() => pick(s)}
								className={cn("h-9 cursor-pointer rounded-lg border px-3.5 text-[13px]", sort === s ? "border-signal bg-raised font-bold text-signal" : "border-line text-ink-2 hover:bg-raised")}
							>
								{name}
							</button>
						))}
					</div>
				))}
				<div role="group" aria-label="Time range" className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
					<span className={cn(label, "mr-1")}>Range</span>
					{RANGES.map(([r, short]) => (
						<button
							key={r}
							type="button"
							aria-pressed={range === r}
							onClick={() => pickRange(r)}
							className={cn("h-9 cursor-pointer rounded-lg border px-3 text-[13px]", range === r ? "border-signal bg-raised font-bold text-signal" : "border-line text-ink-2 hover:bg-raised")}
						>
							{short}
						</button>
					))}
				</div>
			</div>

			{error && (
				<div role="alert" className="mb-4 max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
					{error}
				</div>
			)}

			<div className="rounded-[10px] border border-line bg-surface">
				{data.members.length ? (
					<ol className="m-0 list-none p-0">
						{sorted(data.members, sort).map((m, i) => (
							<Row key={m.id} m={m} place={i + 1} sort={sort} live={live.get(m.id)} onRemove={editKey ? () => act(() => removeLeaderboardMember(slug, range, editKey, m.id), "Could not remove the player") : undefined} />
						))}
					</ol>
				) : (
					<p className="m-0 px-3.5 py-4 text-sm text-ink-2">{editKey ? "No players yet. Add the first one below." : "No players yet."}</p>
				)}
			</div>
			{WEEK[sort] && (
				<p className="mt-2 text-xs text-ink-3">
					{sort === "week" ? "Solo/Duo, Flex and Ranked 5s" : QUEUE_LABEL[queueOf(sort)]} LP over the last {rangeText(range)}, counted from when RiftWatch first saw each rank.
				</p>
			)}

			{data.members.length > 0 && (
				<div className="mt-5 grid gap-3 md:grid-cols-2">
					<GroupsCard groups={data.groups[groupQueue]} queue={groupQueue} members={byId} range={data.range} />
					<FeedCard feed={data.feed} members={byId} range={data.range} />
				</div>
			)}

			{editKey && (
				<div className="mt-5 flex flex-col gap-3">
					<div className={label}>
						Add players · {data.members.length}/{data.maxMembers}
					</div>
					<AddForm disabled={full} onAdd={(region, riotId) => act(() => addLeaderboardMember(slug, range, editKey, region, riotId), "Could not add the player")} />
					<p className="m-0 max-w-[640px] text-xs leading-normal text-ink-3">
						Anyone with the edit link can change this board. Keep it to people you trust and share the view link with everyone else. Lose the edit
						link and the board can no longer be changed.
					</p>
					<button
						type="button"
						className="self-start cursor-pointer text-xs font-semibold text-ink-3 hover:text-ink"
						onClick={() => {
							if (!confirm(`Delete "${data.name}" for everyone? This cannot be undone.`)) return;
							deleteLeaderboard(slug, editKey)
								.then(() => {
									saveBoard(slug, null);
									navigate("/leaderboard");
								})
								.catch(e => setError(errorText(e, "Could not delete the leaderboard")));
						}}
					>
						Delete leaderboard
					</button>
				</div>
			)}
		</>
	);
}

function Start() {
	const navigate = useNavigate();
	const [name, setName] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const saved = savedBoards();

	return (
		<>
			<h1 className="m-0 text-[30px] font-extrabold leading-[1.05] [font-stretch:118%] sm:text-[40px]">Leaderboards.</h1>
			<p className="mt-3 mb-5 max-w-[640px] text-[15px] leading-normal text-ink-2 text-pretty">
				Rank your friends by Solo/Duo, Flex or LP gained, see who queues together and who just climbed. No account needed: you get a view link to share and an edit link to keep.
			</p>
			<form
				className="flex max-w-[520px] flex-wrap gap-2"
				onSubmit={async e => {
					e.preventDefault();
					setBusy(true);
					try {
						const { slug, editKey } = await createLeaderboard(name);
						saveBoard(slug, { name: name.trim(), editKey });
						navigate(`/leaderboard/${slug}`);
					} catch (err) {
						setError(errorText(err, "Could not create the leaderboard"));
						setBusy(false);
					}
				}}
			>
				<input
					value={name}
					onChange={e => setName(e.target.value)}
					maxLength={40}
					placeholder="Leaderboard name"
					aria-label="Leaderboard name"
					className="h-11 min-w-0 flex-[1_1_220px] rounded-lg border border-line-2 bg-raised px-3 text-sm text-ink placeholder:text-ink-3"
				/>
				<button type="submit" disabled={!name.trim() || busy} className={button.primary}>
					{busy ? "Creating…" : "Create"}
				</button>
			</form>
			{error && (
				<div role="alert" className="mt-4 max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
					{error}
				</div>
			)}
			{saved.length > 0 && (
				<div className="mt-9 max-w-[680px]">
					<h2 className={cn(label, "mb-2.5")}>Opened on this device</h2>
					<div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
						{saved.map(b => (
							<Link key={b.slug} to={`/leaderboard/${b.slug}`} className="flex min-h-11 items-center justify-between gap-2 rounded-[10px] border border-line bg-surface p-2.5 text-sm font-semibold transition-colors hover:border-line-3 hover:bg-raised">
								<span className="truncate">{b.name}</span>
								{b.editKey && <span className="text-xs font-medium text-ink-3">can edit</span>}
							</Link>
						))}
					</div>
				</div>
			)}
		</>
	);
}

export function LeaderboardPage() {
	const { slug } = useParams();
	return <section className="pt-8 sm:pt-12">{slug ? <Board key={slug} slug={slug} /> : <Start />}</section>;
}
